import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { auth, card } from '../RealTradePage';

// ─────────────────────────────────────────────
// [RT_VOLUME_V1] 거래량 탭 — 월별 거래건수를 단계별로 합산해 보여준다
//
// [화면 원리]
//   · 경로(전체 › 서울동남 › 강남구 …) = 지금 보고 있는 범위
//   · 나눠 보기 = 막대 하나를 무엇으로 쪼개서 쌓을지 (권역/시/구/동/단지/평형)
//   · 막대 높이 = 그 달 범위 전체 합계 / 막대 조각 = 한 단계 아래 항목
//   · 막대 조각이나 아래 표의 줄을 누르면 그 항목으로 한 단계 들어간다
//
// [연관]
//   - 백엔드 routes/realTrades.js  GET /api/real-trades/volume  [RT_VOLUME_V1]
//   - RealTradePage.jsx 가 공통필터(유형·권역·구·기간)와 pickedApt 를 내려준다
//   - pickedApt: 단지검색에서 단지를 누르면 들어옴 → 이 탭도 그 단지 평형별로 맞춰 둔다
// ─────────────────────────────────────────────

// 단계 순서 (rank 가 클수록 좁은 범위). 나눠 보기는 지금 단계보다 아래만 고를 수 있다.
const LV = {
  all:    { rank: 0, label: '전체' },
  zone:   { rank: 1, label: '권역' },
  si:     { rank: 2, label: '시' },
  gu:     { rank: 3, label: '구' },
  dong:   { rank: 4, label: '동' },
  apt:    { rank: 5, label: '단지' },
  bucket: { rank: 6, label: '평형' },
};
const GROUPS = ['zone', 'si', 'gu', 'dong', 'apt', 'bucket'];

// 각 단계에 들어갔을 때 기본으로 쪼갤 단위
// (권역 → 구: 서울은 한 '시'가 여러 권역에 걸쳐 있어 시로 쪼개면 막대 한 개뿐이라)
const DEFAULT_NEXT = { all: 'zone', zone: 'gu', si: 'gu', gu: 'dong', dong: 'apt', apt: 'bucket' };

const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4',
                '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7'];

// 상단 공통필터로 시작 경로 만들기
function initPath(lawdCd, zone, regions) {
  const p = [{ lv: 'all', f: {}, label: '전체' }];
  if (lawdCd) {
    const r = regions.find(x => x.lawd_cd === lawdCd);
    if (r?.zone) p.push({ lv: 'zone', f: { zone: r.zone }, label: r.zone });
    p.push({ lv: 'gu', f: { lawd_cd: lawdCd }, label: r?.sgg_nm || lawdCd });
  } else if (zone) {
    p.push({ lv: 'zone', f: { zone }, label: zone });
  }
  return p;
}

// 마우스를 올렸을 때 상자: 그 달 합계 + 많은 순 항목
function VolTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload[0]?.payload?.total || 0;
  const items = payload.filter(p => p.value > 0).sort((a, b) => b.value - a.value);
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                  padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {label} · 합계 {total.toLocaleString()}건
      </div>
      {items.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.value.toLocaleString()}건
        </div>
      ))}
    </div>
  );
}

const chip = (on) => ({
  padding: '5px 12px', fontSize: 13, cursor: 'pointer', borderRadius: 14,
  border: on ? '1px solid #3b82f6' : '1px solid #cbd5e1',
  background: on ? '#3b82f6' : '#fff', color: on ? '#fff' : '#475569',
  fontWeight: on ? 600 : 400,
});

export default function TradeVolume({ lawdCd, zone, from, to, regions, propType, pickedApt }) {
  const [path, setPath] = useState(() => initPath(lawdCd, zone, regions));
  const cur = path[path.length - 1];
  const [group, setGroup] = useState(DEFAULT_NEXT[cur.lv] || 'bucket');
  const [top, setTop] = useState(12);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // 단지검색에서 단지를 누르면 → 이 탭도 그 단지의 평형별로 맞춰 둔다
  // (상단 필터와 다른 지역 단지면 무시 — 지역을 바꿨는데 옛 단지로 튀는 것 방지)
  useEffect(() => {
    const a = pickedApt;
    if (!a?.lawd_cd || !a?.umd_nm) return;
    const r = regions.find(x => x.lawd_cd === a.lawd_cd);
    if (lawdCd && lawdCd !== a.lawd_cd) return;
    if (!lawdCd && zone && r?.zone !== zone) return;
    const p = [{ lv: 'all', f: {}, label: '전체' }];
    if (r?.zone) p.push({ lv: 'zone', f: { zone: r.zone }, label: r.zone });
    p.push({ lv: 'gu', f: { lawd_cd: a.lawd_cd }, label: r?.sgg_nm || a.lawd_cd });
    p.push({ lv: 'dong', f: { lawd_cd: a.lawd_cd, umd_nm: a.umd_nm }, label: a.umd_nm });
    p.push({ lv: 'apt', f: { lawd_cd: a.lawd_cd, umd_nm: a.umd_nm, jibun: a.jibun ?? '' },
             label: a.apt_nm || '단지' });
    setPath(p);
    setGroup('bucket');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedApt]);

  const load = useCallback(async () => {
    // 기간 입력 중(예: '2024')에는 조회하지 않는다
    if (!/^\d{6}$/.test(from || '') || !/^\d{6}$/.test(to || '')) return;
    setLoading(true); setErr('');
    try {
      // 경로의 범위 조건을 모두 합친다 (권역 + 구 + 동 … AND)
      const f = Object.assign({}, ...path.map(x => x.f));
      const qs = new URLSearchParams({ group, from, to, prop_type: propType, top: String(top) });
      Object.entries(f).forEach(([k, v]) => {
        if (v !== undefined && v !== null) qs.set(k, v);   // jibun '' 도 그대로 보냄
      });
      const r = await axios.get(`/api/real-trades/volume?${qs.toString()}`, auth());
      setRes(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setRes(null);
    } finally {
      setLoading(false);
    }
  }, [path, group, top, from, to, propType]);

  useEffect(() => { load(); }, [load]);

  // 막대 조각·표 줄 클릭 → 그 항목을 경로에 추가하고 한 단계 아래로 쪼갠다
  const drill = (s) => {
    if (!s.key || group === 'bucket') return;   // 기타·평형은 더 못 들어감
    let item;
    if (group === 'zone')      item = { lv: 'zone', f: { zone: s.key } };
    else if (group === 'si')   item = { lv: 'si',   f: { si: s.key } };
    else if (group === 'gu')   item = { lv: 'gu',   f: { lawd_cd: s.key } };
    else if (group === 'dong') {
      const [l, u] = s.key.split('|');
      item = { lv: 'dong', f: { lawd_cd: l, umd_nm: u } };
    } else if (group === 'apt') {
      const [l, u, j] = s.key.split('|');
      item = { lv: 'apt', f: { lawd_cd: l, umd_nm: u, jibun: j || '' } };
    } else return;
    item.label = s.label;
    setPath([...path, item]);
    setGroup(DEFAULT_NEXT[item.lv] || 'bucket');
  };

  // 경로 클릭 → 그 단계로 되돌아감
  const goPath = (i) => {
    const p = path.slice(0, i + 1);
    setPath(p);
    setGroup(DEFAULT_NEXT[p[p.length - 1].lv] || 'bucket');
  };

  const allowed = GROUPS.filter(g => LV[g].rank > LV[cur.lv].rank);
  const canDrill = group !== 'bucket';
  const data = (res?.rows || []).map(r => ({ ...r, x: `${r.ym.slice(2, 4)}.${r.ym.slice(4, 6)}` }));
  const nMonths = res?.months?.length || 1;

  return (
    <div>
      {/* ── 경로 + 나눠 보기 ── */}
      <div style={card}>
        <div style={{ fontSize: 14, marginBottom: 10 }}>
          {path.map((p, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: '#94a3b8', margin: '0 6px' }}>›</span>}
              <button onClick={() => goPath(i)}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer',
                         fontSize: 14, color: i === path.length - 1 ? '#0f172a' : '#3b82f6',
                         fontWeight: i === path.length - 1 ? 700 : 400 }}>
                {p.label}
              </button>
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#475569', fontWeight: 600, marginRight: 4 }}>나눠 보기</span>
          {allowed.map(g => (
            <button key={g} onClick={() => setGroup(g)} style={chip(group === g)}>
              {LV[g].label}
            </button>
          ))}
          {group !== 'bucket' && (
            <select value={top} onChange={e => setTop(Number(e.target.value))}
                    style={{ marginLeft: 8, padding: '4px 6px', fontSize: 12,
                             border: '1px solid #cbd5e1', borderRadius: 4 }}>
              {[8, 12, 20, 30].map(n => <option key={n} value={n}>상위 {n}개</option>)}
            </select>
          )}
        </div>
      </div>

      {err && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b',
                      borderRadius: 6, marginBottom: 12, fontSize: 13 }}>⚠️ {err}</div>
      )}
      {loading && <div style={{ padding: 12, color: '#3b82f6' }}>조회 중…</div>}

      {res && !loading && (
        <>
          {/* ── 그래프 ── */}
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap',
                          gap: 8, marginBottom: 8 }}>
              <b>📶 월별 거래건수 — {cur.label} · {LV[group].label}별</b>
              <span style={{ fontSize: 13, color: '#475569' }}>
                기간 합계 <b>{res.total.toLocaleString()}</b>건 ·
                월평균 <b>{Math.round(res.total / nMonths).toLocaleString()}</b>건 ·
                {LV[group].label} {res.item_cnt}개
              </span>
            </div>

            {res.total === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                이 범위·기간에는 거래가 없습니다
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="x" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip content={<VolTip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {res.series.map((s, i) => (
                    <Bar key={s.id} dataKey={s.id} name={s.label} stackId="v"
                         fill={s.id === 'etc' ? '#cbd5e1' : COLORS[i % COLORS.length]}
                         cursor={canDrill && s.key ? 'pointer' : 'default'}
                         onClick={() => drill(s)} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {canDrill ? '막대 조각이나 아래 표의 줄을 누르면 한 단계 들어갑니다. ' : ''}
              해제거래 제외 · {res.source === 'rt_apt_trade' ? '원본 거래 기준' : '단지×월 집계표 기준'}
            </div>
          </div>

          {/* ── 항목별 합계 표 ── */}
          {res.total > 0 && (
            <div style={card}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={th}>{LV[group].label}</th>
                    <th style={{ ...th, textAlign: 'right' }}>합계</th>
                    <th style={{ ...th, textAlign: 'right' }}>비중</th>
                    <th style={{ ...th, textAlign: 'right' }}>월평균</th>
                  </tr>
                </thead>
                <tbody>
                  {res.series.map((s, i) => {
                    const click = canDrill && s.key;
                    return (
                      <tr key={s.id} onClick={() => click && drill(s)}
                          style={{ cursor: click ? 'pointer' : 'default', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={td}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                                         marginRight: 6, verticalAlign: 'middle',
                                         background: s.id === 'etc' ? '#cbd5e1' : COLORS[i % COLORS.length] }} />
                          <span style={{ color: click ? '#1d4ed8' : '#334155' }}>{s.label}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{s.total.toLocaleString()}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {(s.total / res.total * 100).toFixed(1)}%
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {(s.total / nMonths).toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569' };
const td = { padding: '7px 10px' };
